import React from 'react'
import { Form, Row, Col, DropdownButton, Dropdown } from 'react-bootstrap'

// definitions
import { AccountXeroOrgWithData } from '../../../../screens/subsync/subsync'
import { XeroAPIContact } from '../../../../../../back-end/utilities/apiDefinitions'
import { MessageAction } from '../../../ui/messages/message'
import { AppState } from '../../../../App.d'

// utilities
import { XeroContactLiveSearch } from './XeroContactLiveSearch'
import { DetailedStripeOrgAccountXeroOrg } from '../settings'

interface StripeFeesSettingsProps {
	orgMapping: DetailedStripeOrgAccountXeroOrg | undefined
	mappedXeroOrg: AccountXeroOrgWithData | undefined
	stripeOrgID: string
	xeroOrgs: AccountXeroOrgWithData[]
	appState: AppState
	handleSettingsUpdate: (stripeOrgID: string, values: { [name: string]: string | boolean | XeroAPIContact | undefined }) => void
	updateMessages: (action: MessageAction) => void
}

const StripeFeesSettings = (props: StripeFeesSettingsProps) => {
	return (
		<>
			{props.orgMapping?.stripeOrgAccountXeroOrgProcessingType === 'Standard' && (
				<>
					<Form.Group as={Row} className="d-flex align-items-center" style={{ height: '2.5rem' }}>
						<Col sm="2">
							<Form.Label>Enable Bills for Stripe Fees</Form.Label>
						</Col>
						<Col>
							<Form.Check
								type={'switch'}
								id={`${props.stripeOrgID}-stripe-fees-bills-enabled`}
								name={'stripeFeesBillsEnabled'}
								checked={!!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesBillsEnabled}
								onChange={(e) => props.handleSettingsUpdate(props.stripeOrgID, { [e.target.name]: e.target.checked })}
							/>
						</Col>
					</Form.Group>
					<Form.Group as={Row}>
						<Form.Label column sm={2}>
							Xero Contact
						</Form.Label>
						{(props.orgMapping.stripeOrgAccountXeroOrgSettings?.stripeFeesXeroContact ||
							props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesXeroContactID) && (
							<Col sm={3} className="align-self-center">
								{props.orgMapping.stripeOrgAccountXeroOrgSettings?.stripeFeesXeroContact?.name ||
									props.mappedXeroOrg!.contacts.find(
										(c) => c.xeroContactID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesXeroContactID
									)?.name}
							</Col>
						)}
						<XeroContactLiveSearch
							onConfirm={(values) => {
								props.handleSettingsUpdate(props.stripeOrgID, {
									stripeFeesXeroContactXeroID: values.xeroContactID,
									stripeFeesXeroContact: values.xeroContact,
								})
							}}
							accountXeroOrg={props.mappedXeroOrg!}
							appState={props.appState}
							updateMessages={props.updateMessages}
							disabled={!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesBillsEnabled}
						/>
					</Form.Group>
				</>
			)}
			<Form.Group as={Row} style={{ height: '2.5rem' }}>
				<Form.Label column sm={2}>
					Xero Account
				</Form.Label>
				<Col sm={4}>
					<DropdownButton
						id={`${props.stripeOrgID}-stripe-fees-bills-account-select`}
						title={
							props.mappedXeroOrg!.accounts.find(
								(a) => a.xeroAccountID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesXeroAccountID
							)?.name || 'Select Xero Account'
						}
						onSelect={(eventKey) => props.handleSettingsUpdate(props.stripeOrgID, { stripeFeesXeroAccountID: eventKey! })}
						disabled={
							props.orgMapping?.stripeOrgAccountXeroOrgProcessingType === 'Standard' &&
							!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesBillsEnabled
						}
					>
						<Dropdown.Header>Direct Costs</Dropdown.Header>
						{props
							.mappedXeroOrg!.accounts.filter((account) => account.type === 'DIRECTCOSTS')
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((account) => (
								<Dropdown.Item key={account.xeroAccountID} eventKey={account.xeroAccountID}>
									{account.name}
								</Dropdown.Item>
							))}
						<Dropdown.Header>Expenses</Dropdown.Header>
						{props
							.mappedXeroOrg!.accounts.filter((account) => account.type === 'EXPENSE')
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((account) => (
								<Dropdown.Item key={account.xeroAccountID} eventKey={account.xeroAccountID}>
									{account.name}
								</Dropdown.Item>
							))}
						<Dropdown.Header>Overheads</Dropdown.Header>
						{props
							.mappedXeroOrg!.accounts.filter((account) => account.type === 'OVERHEADS')
							.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { numeric: true }))
							.map((account) => (
								<Dropdown.Item key={account.xeroAccountID} eventKey={account.xeroAccountID}>
									{account.name}
								</Dropdown.Item>
							))}
					</DropdownButton>
				</Col>
			</Form.Group>
			<Form.Group as={Row} style={{ height: '2.5rem' }}>
				<Form.Label column sm={2}>
					Xero Tax Rate
				</Form.Label>
				<Col sm={4}>
					<DropdownButton
						id={`${props.stripeOrgID}-stripe-fees-bills-tax-rate-select`}
						title={
							props.mappedXeroOrg!.taxRates.find(
								(tr) => tr.xeroTaxRateID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesXeroTaxRateID
							)?.name || 'Select Xero Tax Rate'
						}
						onSelect={(eventKey) => props.handleSettingsUpdate(props.stripeOrgID, { stripeFeesXeroTaxRateID: eventKey! })}
						disabled={
							props.orgMapping?.stripeOrgAccountXeroOrgProcessingType === 'Standard' &&
							!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.stripeFeesBillsEnabled
						}
					>
						{props
							.mappedXeroOrg!.taxRates.filter((tr) => tr.canApplyToExpenses)
							.map((taxRate) => (
								<Dropdown.Item key={taxRate.xeroTaxRateID} eventKey={taxRate.xeroTaxRateID}>
									{taxRate.name} {taxRate.effectiveRate ? `(${(Number(taxRate.effectiveRate) * 100).toFixed(2)}%)` : ''}
								</Dropdown.Item>
							))}
					</DropdownButton>
				</Col>
			</Form.Group>
		</>
	)
}

export { StripeFeesSettings }
